import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Anchor, CornerDownRight, Lock, Clock, Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABELS = { new: "New", in_progress: "In progress", resolved: "Resolved", wont_fix: "Won't fix" };
const STATUS_STYLE = {
  new: "bg-orange text-white", in_progress: "bg-sunny text-ink",
  resolved: "bg-grass text-white", wont_fix: "bg-ink text-cream",
};

function timeStr(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function PublicSubmission() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/public/submission/${token}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/public/submission/${token}/reply`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }),
      });
      if (!r.ok) throw new Error();
      setReply("");
      load();
    } catch { /* noop */ }
    finally { setSending(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink" /></div>;
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6 text-center">
        <Anchor className="h-10 w-10 text-ink/30 mb-3" />
        <h1 className="font-display text-2xl font-800">Submission not found</h1>
        <p className="text-ink/60 font-600 mt-1">This link may be invalid or expired.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Anchor className="h-6 w-6 text-orange" />
          <span className="font-display text-xl font-800">Tugboat</span>
          {data.project_name && <span className="text-ink/50 font-700 text-sm">· {data.project_name}</span>}
        </div>

        <div className="bg-white border-2 border-ink rounded-3xl shadow-nb-xl p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="label-overline">Your submission</p>
              <h1 className="font-display text-3xl font-800">#{data.number}</h1>
            </div>
            <span data-testid="public-status-badge"
              className={`text-xs font-800 uppercase px-3 py-1.5 rounded-full border-2 border-ink ${STATUS_STYLE[data.status] || "bg-white"}`}>
              {data.status_label || STATUS_LABELS[data.status] || data.status}
            </span>
          </div>

          {data.subject && <span className="inline-block text-xs font-800 px-3 py-1 rounded-full border-2 border-ink bg-sunny mb-3">{data.subject}</span>}
          {data.text_note && (
            <p className="text-sm bg-sand border-2 border-ink p-3 rounded-2xl whitespace-pre-wrap font-600 mb-2">{data.text_note}</p>
          )}
          <p className="text-xs text-ink/45 font-600">Submitted {timeStr(data.created_at)}</p>

          <div className="border-t-2 border-ink/10 my-5" />

          <p className="label-overline mb-3">Conversation</p>
          {(data.timeline || []).length === 0 ? (
            <p className="text-sm text-ink/40 font-600">No updates yet — we'll email you when there's news.</p>
          ) : (
            <ul className="space-y-2.5" data-testid="public-timeline">
              {data.timeline.map((a) => {
                if (a.action === "status_changed") {
                  return (
                    <li key={a.id} className="flex items-center gap-2 text-xs font-700 text-ink/70">
                      <CheckCircle2 className="h-3.5 w-3.5 text-grass shrink-0" />
                      Status updated to <b>{STATUS_LABELS[a.to_val] || a.to_val}</b>
                      <span className="ml-auto text-ink/40 font-600">{timeStr(a.created_at)}</span>
                    </li>
                  );
                }
                if (a.kind === "reply") {
                  const tester = a.actor_type === "tester";
                  return (
                    <li key={a.id} className={`border-2 border-ink rounded-2xl p-3 ${tester ? "bg-sand" : "bg-teal/25"}`}>
                      <div className="flex items-center gap-1.5 text-xs font-800">
                        <CornerDownRight className="h-3 w-3" /> {tester ? "You" : (a.actor_name || "Team")}
                        <span className="ml-auto text-ink/45 font-600 flex items-center gap-1"><Clock className="h-3 w-3" />{timeStr(a.created_at)}</span>
                      </div>
                      <p className="text-sm font-600 whitespace-pre-wrap mt-1.5">{a.body}</p>
                    </li>
                  );
                }
                if (a.action === "created") {
                  return (
                    <li key={a.id} className="flex items-center gap-2 text-xs font-700 text-ink/50">
                      <span className="h-2 w-2 rounded-full bg-ink/30" /> You submitted this
                      <span className="ml-auto text-ink/40 font-600">{timeStr(a.created_at)}</span>
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          )}

          <div className="mt-5 border-2 border-ink rounded-2xl p-2.5 bg-white">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} data-testid="public-reply-input"
              placeholder="Add more details or reply to the team…"
              className="w-full text-sm font-600 border-2 border-ink/25 focus:border-ink rounded-xl p-2.5 outline-none resize-none" />
            <div className="flex justify-end mt-1.5">
              <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="public-reply-send"
                className="nb-btn bg-orange text-white text-sm px-4 py-2 disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Send</>}
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-ink/40 font-600 mt-5">Powered by Tugboat · beta feedback, hauled in.</p>
      </div>
    </div>
  );
}
